import type { InstrumentGroupId } from "./instruments";
import type {
  LatestNotificationTransport,
  MaxRebatePerLotTransport,
  MaxRebateTierTransport,
  RebateDetailsTransport,
  RebateTierLadderTransport,
  RebateTierTransport,
  VolumeUnitsTransport,
} from "./transport";
import type {
  AccountRates,
  Criterion,
  CriterionMonth,
  CriterionProgress,
  LadderTier,
  LadderTierKey,
  QualifyingWindow,
  RebateDetails,
  RebateDetailsSection,
  RebateTier,
  RebateTierLadder,
  RebateTierNotice,
  RebateTierNoticeSection,
  RebateTierWidgetModel,
  Region,
  SectionState,
  TierEvent,
  TierStatus,
  VolumeConverterData,
  VolumeUnit,
  VolumeUnitOption,
} from "./types";

const TRANSPORT_CRITERION: Record<string, Criterion> = {
  VolumeUSD: "tv",
  ActiveClients: "ac",
  NewClients: "nc",
};

const CRITERIA: Criterion[] = ["tv", "ac", "nc"];

export const TIER_NOTIFICATION_TYPES = [
  "TIER_UPGRADED",
  "TIER_DOWNGRADED",
  "TIER_AT_RISK",
  "GP_ASSIGNED",
  "GP_EXTENDED",
  "GP_EXPIRED",
  "TF_ASSIGNED",
  "TF_EXTENDED",
  "TF_UNASSIGNED",
  "FP_EXPIRED",
] as const;

const NOTIFICATION_EVENT: Record<
  (typeof TIER_NOTIFICATION_TYPES)[number],
  Exclude<TierEvent, null> | null
> = {
  TIER_UPGRADED: "upgrade",
  TIER_DOWNGRADED: "downgrade",
  TIER_AT_RISK: "atRisk",
  GP_ASSIGNED: "grace",
  GP_EXTENDED: "grace",
  // Requested so it supersedes an older GP_ASSIGNED in /latest; it has no strip of its own.
  GP_EXPIRED: null,
  TF_ASSIGNED: "freeze",
  TF_EXTENDED: "freeze",
  TF_UNASSIGNED: "unfreeze",
  FP_EXPIRED: "unfreeze",
};

const ASSET_GROUP: Record<string, InstrumentGroupId> = {
  fx: "fx",
  commodity: "commodity",
  cfd: "cfd",
  equity: "equity",
  crypto: "crypto",
  treasuries: "treasuries",
  spreadbetting: "spreadBetting",
};

const MS_PER_DAY = 86_400_000;

const isPeriodActive = (validTo: string | null, now: Date): boolean => {
  if (!validTo) return false;
  const expiry = new Date(validTo).getTime();
  return !Number.isNaN(expiry) && expiry > now.getTime();
};

export const isTierAutomationApplied = (
  transport: RebateTierTransport,
): boolean =>
  transport.partnerTierID !== "" &&
  transport.tierStatus !== "TA disabled" &&
  transport.tierName !== "Custom";

const deriveStatus = (
  transport: RebateTierTransport,
  now: Date,
): TierStatus => {
  if (transport.tierStatus === "Fraudulent") return "fraudulent";
  if (
    isPeriodActive(transport.freezePeriodValidTo, now) ||
    transport.tierStatus === "Tier frozen"
  )
    return "frozen";
  if (isPeriodActive(transport.gracePeriodValidTo, now)) return "grace";
  if (transport.tierStatus === "Upgrading") return "upgrading";
  return "maintaining";
};

const monthsFor = (
  transport: RebateTierTransport,
  criterion: Criterion,
): CriterionMonth[] => {
  const entry = transport.criteriaProgress?.find(
    (row) => TRANSPORT_CRITERION[row.type] === criterion,
  );
  return [...(entry?.months ?? [])].sort((a, b) =>
    a.month.localeCompare(b.month),
  );
};

const criterionCurrent = (
  transport: RebateTierTransport,
  criterion: Criterion,
  months: CriterionMonth[],
): number => {
  const sum = months.reduce((total, row) => total + row.value, 0);
  const latest = months.at(-1)?.value ?? 0;

  if (criterion === "tv") return transport.tradeVolumeUSD ?? sum;
  if (criterion === "ac") return transport.activeClients ?? latest;
  return sum;
};

const deriveQualifyingWindow = (
  transport: RebateTierTransport,
): QualifyingWindow | null => {
  const allMonths = (transport.criteriaProgress ?? [])
    .flatMap((entry) => entry.months ?? [])
    .map((row) => row.month)
    .sort((a, b) => a.localeCompare(b));

  const from = allMonths.at(0);
  const to = allMonths.at(-1);
  return from && to ? { from, to } : null;
};

const deriveCycleEnd = (window: QualifyingWindow | null): string | null => {
  if (!window) return null;
  const [year, month] = window.to.split("-").map(Number);
  if (!year || !month) return null;
  return new Date(Date.UTC(year, month, 1)).toISOString();
};

export const daysUntil = (
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null => {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - now.getTime()) / MS_PER_DAY));
};

export const countdownDays = (days: number | null): number | null =>
  days === null ? null : Math.max(1, days);

export const toLadderTierKey = (
  name: string | null | undefined,
): LadderTierKey | null => {
  const key = name?.trim().toLowerCase();
  return key ? key : null;
};

/** The tier whose thresholds a partner at `tierRank` is measured against. */
export const targetTier = (
  ladder: RebateTierLadder,
  tierRank: number,
): LadderTier | undefined =>
  ladder.tiers.find((tier) => tier.rank > tierRank) ?? ladder.tiers.at(-1);

export const isFinalTier = (
  ladder: RebateTierLadder,
  tierRank: number,
): boolean =>
  ladder.tiers.length > 0 && !ladder.tiers.some((tier) => tier.rank > tierRank);

/** The next tier while upgrading; otherwise the tier held, whose thresholds must be maintained. */
export const criteriaTargetTier = (
  ladder: RebateTierLadder,
  tierRank: number,
  status: TierStatus,
): LadderTier | undefined =>
  status === "upgrading"
    ? targetTier(ladder, tierRank)
    : (ladder.tiers.find((tier) => tier.rank === tierRank) ??
      targetTier(ladder, tierRank));

export const toCriteriaProgress = (
  transport: RebateTierTransport,
  ladder: RebateTierLadder,
  now: Date = new Date(),
): CriterionProgress[] => {
  const target = criteriaTargetTier(
    ladder,
    transport.tierRank,
    deriveStatus(transport, now),
  );

  return ladder.criteria.map((criterion) => {
    const months = monthsFor(transport, criterion);
    return {
      criterion,
      current: criterionCurrent(transport, criterion, months),
      target: target?.[criterion] ?? 0,
      months,
    };
  });
};

const findMaxRebate = (
  maxRebate: MaxRebatePerLotTransport | undefined,
  predicate: (tier: MaxRebateTierTransport) => boolean,
): number | null => maxRebate?.tiers?.find(predicate)?.maxRebatePerLot ?? null;

export const toRebateTierViewModel = (
  transport: RebateTierTransport,
  region: Region,
  now: Date = new Date(),
  maxRebate?: MaxRebatePerLotTransport,
): RebateTier => {
  const qualifyingWindow = deriveQualifyingWindow(transport);
  const tierKey = toLadderTierKey(transport.tierName);
  const maxRebatePerLot =
    findMaxRebate(maxRebate, (tier) => tier.rank === transport.tierRank) ??
    findMaxRebate(maxRebate, (tier) => tier.key === tierKey);

  return {
    region,
    tierName: transport.tierName,
    tierRank: transport.tierRank,
    tierKey,
    isExclusive: transport.isExclusive,
    tierVisible: transport.isTierVisible,
    maxRebatePerLot,
    maxRebateCurrency:
      maxRebatePerLot === null ? null : (maxRebate?.currency ?? null),
    qualifiedTierName: transport.qualifiedTierName,
    qualifiedTierRank: transport.qualifiedTierRank,
    criteria: [],
    status: deriveStatus(transport, now),
    freezePeriodValidTo: transport.freezePeriodValidTo,
    gracePeriodValidTo: transport.gracePeriodValidTo,
    cycleEndsAt: deriveCycleEnd(qualifyingWindow),
    qualifyingWindow,
    eventNotification: transport.isOnboardingGrace,
    lastUpdated: transport.evaluatedAt ?? null,
  };
};

/** `null` when the ladder has no renderable tier (e.g. an unresolved region). */
export const toRebateTierLadder = (
  transport: RebateTierLadderTransport,
  maxRebate?: MaxRebatePerLotTransport,
): RebateTierLadder | null => {
  const tiers = (transport.tiers ?? []).flatMap<LadderTier>((tier) => {
    const key = toLadderTierKey(tier.key);
    if (!key) return [];
    const maxRebatePerLot = findMaxRebate(
      maxRebate,
      (candidate) => toLadderTierKey(candidate.key) === key,
    );
    return [
      {
        key,
        rank: tier.rank,
        tv: tier.tv ?? 0,
        ...(tier.ac !== undefined && { ac: tier.ac }),
        ...(tier.nc !== undefined && { nc: tier.nc }),
        ...(tier.rebateDefaultSetId !== undefined && {
          defaultSetId: tier.rebateDefaultSetId,
        }),
        ...(maxRebatePerLot !== null && { maxRebatePerLot }),
      },
    ];
  });

  if (tiers.length === 0) return null;

  return {
    criteria: CRITERIA.filter((criterion) =>
      transport.criteria?.includes(criterion),
    ),
    ...(maxRebate &&
      tiers.some((tier) => tier.maxRebatePerLot !== undefined) && {
        currency: maxRebate.currency,
      }),
    tiers,
  };
};

const isTierNotificationType = (
  type: string,
): type is (typeof TIER_NOTIFICATION_TYPES)[number] =>
  (TIER_NOTIFICATION_TYPES as readonly string[]).includes(type);

export const toTierNotice = (
  transport: LatestNotificationTransport | null,
): RebateTierNotice | null => {
  if (!transport || transport.dismissedAt !== null) return null;
  if (!isTierNotificationType(transport.type)) return null;
  const event = NOTIFICATION_EVENT[transport.type];
  return event ? { id: transport.id, event } : null;
};

const toAccountRates = (rates: {
  standardValue: number;
  razorValue: number;
  islamicValue: number;
}): AccountRates => ({
  standard: rates.standardValue,
  razor: rates.razorValue,
  islamic: rates.islamicValue,
});

export const toInstrumentGroupId = (asset: string): InstrumentGroupId | null =>
  ASSET_GROUP[asset.toLowerCase().replace(/[^a-z]/g, "")] ?? null;

export const toRebateDetails = (
  transport: RebateDetailsTransport,
): RebateDetails => ({
  groups: (transport.assets ?? []).map((asset) => ({
    asset: asset.asset,
    groupId: toInstrumentGroupId(asset.asset),
    rates: toAccountRates(asset),
    symbols: (asset.symbols ?? []).map((symbol) => ({
      symbol: symbol.symbol,
      rates: toAccountRates(symbol),
    })),
  })),
  currency: transport.currency,
});

const USD_VOLUME_UNIT = "USD";

export const toVolumeUnitValue = (unit: VolumeUnit): string =>
  unit.kind === "usd" ? USD_VOLUME_UNIT : unit.symbol;

/** A missing or failed `/volume-units` offers USD only, not an error state. */
export const toVolumeUnitOptions = (
  transport: VolumeUnitsTransport | undefined,
): Pick<VolumeConverterData, "options" | "rateDate"> => ({
  options: (transport?.options ?? []).map(({ symbol, perLotUsd }) => ({
    symbol,
    perLotUsd,
  })),
  rateDate: transport?.rateDate ?? null,
});

export const toSavedVolumeUnit = (
  value: string | null | undefined,
  options: VolumeUnitOption[],
): VolumeUnit => {
  const option = options.find(({ symbol }) => symbol === value);
  return option ? { kind: "instrument", ...option } : { kind: "usd" };
};

export type RebateTierWidgetSources = {
  region: Region;
  profileComplete: boolean;
  now?: Date;
  tier: SectionState<RebateTierTransport>;
  ladder: SectionState<RebateTierLadderTransport>;
  maxRebate: SectionState<MaxRebatePerLotTransport>;
  notice: SectionState<LatestNotificationTransport | null> &
    Pick<RebateTierNoticeSection, "dismiss">;
  rebateDetails: SectionState<RebateDetailsTransport> &
    Pick<RebateDetailsSection, "request" | "loadedSetId">;
  volumeUnits: SectionState<VolumeUnitsTransport>;
  saveDefaultVolumeUnit: RebateTierWidgetModel["saveDefaultVolumeUnit"];
};

/**
 * The single composition path from the six sources to the widget model. Both
 * the portal's live hook and the fixture-backed Storybook hook feed this.
 */
export const composeRebateTierWidgetModel = ({
  region,
  profileComplete,
  now,
  tier,
  ladder,
  maxRebate,
  notice,
  rebateDetails,
  volumeUnits,
  saveDefaultVolumeUnit,
}: RebateTierWidgetSources): RebateTierWidgetModel => {
  const volumeUnitOptions = toVolumeUnitOptions(volumeUnits.data);
  const ladderData = ladder.data
    ? toRebateTierLadder(ladder.data, maxRebate.data)
    : null;
  const tierData = tier.data
    ? {
        ...toRebateTierViewModel(tier.data, region, now, maxRebate.data),
        criteria: ladderData
          ? toCriteriaProgress(tier.data, ladderData, now)
          : [],
      }
    : undefined;

  return {
    profileComplete,
    tier: { ...tier, data: tierData },
    ladder: {
      ...ladder,
      data: ladderData ?? undefined,
      isError: ladder.isError || (ladder.data !== undefined && !ladderData),
    },
    notice: {
      ...notice,
      data: notice.data === undefined ? undefined : toTierNotice(notice.data),
    },
    rebateDetails: {
      ...rebateDetails,
      data: rebateDetails.data
        ? toRebateDetails(rebateDetails.data)
        : undefined,
    },
    volumeConverter: {
      ...volumeUnitOptions,
      savedDefault: toSavedVolumeUnit(
        tier.data?.defaultVolumeUnit,
        volumeUnitOptions.options,
      ),
    },
    saveDefaultVolumeUnit,
  };
};
